/**
 * ADR-0230 — one decided proposal in the Agent registry history.
 *
 * Automatic approvals carry an "Auto" badge; every approved identity has a
 * Revoke action (reason required) that removes it from the edge registry.
 */

import { useState } from 'react';

import type { AdminAgentRegistryProposal } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { categoryLabel, decisionError, isAutoDecided } from './registry-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface HistoryRowProps {
  readonly proposal: AdminAgentRegistryProposal;
  readonly onChanged: () => void;
}

export function HistoryRow({ proposal: p, onChanged }: HistoryRowProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auto = isAutoDecided(p);
  const kvDeletePending = p.status === 'revoked' && p.approved_category !== 'hostile' && p.kv_deleted_at === null;

  const revoke = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.revokeAgentProposal(p.proposal_id, reason.trim() || (kvDeletePending ? 'retry edge delete' : ''));
      setRevoking(false);
      onChanged();
    } catch (err) {
      setError(decisionError(err));
    } finally {
      setBusy(false);
    }
  };

  const tone = p.status === 'approved' ? 'lime' : p.status === 'rejected' || p.status === 'revoked' ? 'coral' : 'neutral';
  return (
    <div data-testid="history-row" style={{ padding: '8px 4px', borderBottom: `1px solid ${C.border}`, fontFamily: sansFont, fontSize: 12.5, color: C.body }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Pill tone={tone}>{p.status}</Pill>
        {auto ? <Pill tone="sky">Auto</Pill> : null}
        <span style={{ color: C.text }}>{p.approved_display_name ?? p.display_name ?? p.candidate_key}</span>
        {p.approved_agent_id !== null ? <span style={{ fontFamily: monoFont, fontSize: 11 }}>{p.approved_agent_id}</span> : null}
        {p.approved_category !== null ? <span>{categoryLabel(p.approved_category)}</span> : null}
        {p.approved_operator !== null ? <span style={{ color: C.muted }}>{p.approved_operator}</span> : null}
        {p.status === 'approved' && p.approved_category !== 'hostile' ? (
          p.kv_written_at !== null ? <span style={{ color: C.muted }}>edge registry written</span> : <span style={{ color: C.coralFg }}>edge write pending{p.kv_error !== null ? ` (${p.kv_error})` : ''}</span>
        ) : null}
        {p.status === 'revoked' ? (
          kvDeletePending ? <span style={{ color: C.coralFg }}>edge removal pending{p.kv_error !== null ? ` (${p.kv_error})` : ''}</span> : <span style={{ color: C.muted }}>removed from the edge registry</span>
        ) : null}
        {p.decision_note !== null ? <span style={{ color: C.muted }}>“{p.decision_note}”</span> : null}
        <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
          {p.decided_by === 'auto' ? 'automatic (signed)' : p.decided_by === 'auto_ai' ? 'automatic (AI)' : p.decided_by !== null ? `by ${p.decided_by}` : ''}
          {p.decided_at !== null ? ` · ${formatIsoCompact(p.decided_at)}` : ''}
          {p.revoked_at !== null ? ` · revoked ${formatIsoCompact(p.revoked_at)}${p.revoked_by !== null ? ` by ${p.revoked_by}` : ''}` : ''}
        </span>
        {p.status === 'approved' && !revoking ? (
          <button
            type="button"
            onClick={() => setRevoking(true)}
            style={{ marginLeft: 'auto', fontFamily: sansFont, fontSize: 12, padding: '4px 10px', background: 'transparent', color: C.coralFg, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
          >
            Revoke
          </button>
        ) : null}
        {kvDeletePending ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void revoke()}
            style={{ marginLeft: 'auto', fontFamily: sansFont, fontSize: 12, padding: '4px 10px', background: 'transparent', color: C.coralFg, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
          >
            Retry edge removal
          </button>
        ) : null}
      </div>
      {revoking ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            aria-label="Revoke reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why revoke?"
            style={{ fontFamily: sansFont, fontSize: 12.5, padding: '5px 8px', background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, width: 260 }}
          />
          <button
            type="button"
            disabled={busy || reason.trim().length === 0}
            onClick={() => void revoke()}
            style={{ fontFamily: sansFont, fontSize: 12.5, fontWeight: 600, padding: '5px 12px', background: 'transparent', color: C.coralFg, border: `1px solid ${C.coralFg}`, borderRadius: 3, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}
          >
            Confirm revoke
          </button>
          <button type="button" disabled={busy} onClick={() => setRevoking(false)} style={{ fontFamily: sansFont, fontSize: 12.5, padding: '5px 12px', background: 'transparent', color: C.body, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}>
            Cancel
          </button>
          <span style={{ fontSize: 11.5, color: C.muted }}>Removes the name from the edge registry within about 5 minutes. Automatic will not approve it again.</span>
        </div>
      ) : null}
      {error !== null ? (
        <div role="alert" style={{ marginTop: 6, color: C.coralFg }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
